<template>
    <form
        class="relative block w-full"
        v-click-outside="() => (hideSuggestions = true)"
    >
        <label class="relative block w-full">
            <span class="sr-only">Search</span>
            <input
                type="text"
                v-model="model"
                class="block w-full bg-white rounded border-none py-1.5 text-slate-500 text-sm placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :placeholder="placeholder"
                v-debounce="350"
                @input="handleChange"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown.enter.prevent="handleSubmit"
                @keydown.down="navigate(1)"
                @keydown.up="navigate(-1)"
                autocomplete="off"
            />
            <button
                class="absolute right-2 top-1/2 transform -translate-y-1/2 z-10"
                @click="handleClear"
                v-if="model"
            >
                <Icon icon="close" class="text-slate-500" />
            </button>
        </label>
        <div
            class="absolute left-0 right-0 top-full z-20 pt-2"
            v-if="filteredSuggestions.length && inputFocused"
        >
            <ul
                class="w-full sm:min-w-max max-h-40 min-h-16 overflow-y-auto p-2 bg-white rounded-lg shadow relative"
            >
                <li
                    v-for="(suggestion, index) in filteredSuggestions"
                    :key="suggestion"
                    @mousedown.prevent="selectSuggestion(index)"
                    class="w-full cursor-pointer p-2 hover:bg-slate-100 transition-colors gap-2 rounded-md flex items-center justify-between"
                    :class="{ 'bg-slate-100': index === selectedIndex }"
                    role="button"
                >
                    <div
                        class="w-5 h-5 justify-center items-center flex text-slate-800"
                        v-if="dropdownName === 'airport'"
                    >
                        <div
                            class="w-5 h-5 relative flex-col justify-start items-start flex"
                        >
                            <LoungeIcon
                                icon="airplane"
                                v-if="!suggestion.recentSearch"
                            />
                            <Icon icon="history" v-else />
                        </div>
                    </div>
                    <div
                        class="grow shrink basis-0 flex-col justify-center items-start gap-1 flex"
                    >
                        <div
                            class="text-slate-800 text-sm font-normal leading-tight"
                        >
                            {{ suggestion[displayKey] }}
                        </div>
                        <div
                            class="self-stretch text-slate-500 text-xs font-normal leading-none"
                            v-if="dropdownName === 'airport'"
                        >
                            {{ suggestion.city }},
                            {{ suggestion.country }}
                        </div>
                    </div>
                </li>
                <li
                    class="w-full p-4 gap-2 rounded-md flex"
                    v-if="!filteredSuggestions.length"
                >
                    No results found, try adjusting your search.
                </li>
            </ul>
        </div>
    </form>
</template>
<script setup>
import { computed, ref, watch } from 'vue';
import vClickOutside from 'click-outside-vue3';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    allSuggestions: {
        type: Array,
        required: true,
    },
    recentSearches: {
        type: Array,
        required: false,
        default: [],
    },
    inputValue: {
        type: String,
        required: false,
    },
    placeholder: {
        type: String,
        default: 'Search by airport code or name',
    },
    dropdownName: {
        type: String,
        default: 'airport',
    },
    displayKey: {
        type: String,
        default: 'name',
    },
    valueKey: {
        type: String,
        default: 'value',
    },
    // inputClass: {
    //     type: String,
    //     default:
    //         'border-none w-full focus:ring-0 relative text-slate-500 text-sm font-normal leading-tight placeholder:text-slate-500 placeholder:font-normal',
    // },
});

const emit = defineEmits(['submit', 'clear', 'select', 'change']);
const model = ref('');
const selectedIndex = ref(-1);
const inputFocused = ref(false);

const filteredSuggestions = computed(() => {
    const uniqueMap = new Map();

    props.recentSearches.forEach((item) => {
        uniqueMap.set(item.code, { ...item, recentSearch: true });
    });

    props.allSuggestions.forEach((item) => {
        if (!uniqueMap.has(item.code)) {
            uniqueMap.set(item.code, { ...item, recentSearch: false });
        }
    });

    const allItems = Array.from(uniqueMap.values());

    if (model.value) {
        const s = model.value.toLowerCase();
        const filtered = allItems.filter(
            (item) =>
                item[props.displayKey]?.toLowerCase().includes(s) ||
                item[props.valueKey]?.toLowerCase().includes(s)
        );
        return filtered.sort((a, b) => {
            if (a.recentSearch !== b.recentSearch) {
                return a.recentSearch ? -1 : 1;
            }
            const aCode = a[props.valueKey]?.toLowerCase() || '';
            const bCode = b[props.valueKey]?.toLowerCase() || '';
            const aName = a[props.displayKey]?.toLowerCase() || '';
            const bName = b[props.displayKey]?.toLowerCase() || '';
            if (aCode === s && bCode !== s) return -1;
            if (aCode !== s && bCode === s) return 1;
            if (aCode.startsWith(s) && !bCode.startsWith(s)) return -1;
            if (bCode.startsWith(s) && !aCode.startsWith(s)) return 1;

            if (aName.includes(s) && !bName.includes(s)) return -1;
            if (bName.includes(s) && !aName.includes(s)) return 1;

            return aCode.localeCompare(bCode);
        });
    }

    return allItems;
});

const selectSuggestion = (index = selectedIndex.value) => {
    if (index >= 0 && index < filteredSuggestions.value.length) {
        emit('select', filteredSuggestions.value[index]);
        model.value = '';
        selectedIndex.value = -1;
        inputFocused.value = false;
    }
};
const handleChange = () => {
    emit('change', model.value);
    if (filteredSuggestions.value.length) {
        selectedIndex.value = 0;
    }
};

const handleClear = () => {
    model.value = '';
    selectedIndex.value = -1;
    emit('clear', model.value);
};

const handleBlur = () => {
    inputFocused.value = false;
};

const handleFocus = () => {
    selectedIndex.value = 0;
    inputFocused.value = true;
};

const handleSubmit = () => {
    selectSuggestion();
};

const navigate = (direction) => {
    if (!filteredSuggestions.value.length) return;
    selectedIndex.value =
        (selectedIndex.value + direction + filteredSuggestions.value.length) %
        filteredSuggestions.value.length;
};

watch(
    () => props.inputValue,
    () => {
        model.value = props.inputValue;
    },
    {
        immediate: true,
    }
);
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
